import {
  allowsChannelNotification,
  type ChannelNotificationMode,
} from '@/convex/organizations/channels/notification_policy.helpers';

export function isChannelMuted(
  mutedUntil: number | null,
  now = Date.now()
): boolean {
  return mutedUntil !== null && mutedUntil > now;
}

export function shouldToastChannelMention({
  activeChannelId,
  channelId,
  currentMentionCount,
  mutedUntil,
  now = Date.now(),
  previousMentionCount,
}: {
  activeChannelId: string;
  channelId: string;
  currentMentionCount: number;
  mutedUntil: number | null;
  now?: number;
  previousMentionCount: number;
}): boolean {
  return (
    currentMentionCount > previousMentionCount &&
    channelId !== activeChannelId &&
    !isChannelMuted(mutedUntil, now)
  );
}

export function compareChannelPreferenceOrder(
  left: { pinned: boolean; lastMessageAt: number | null; createdAt: number },
  right: { pinned: boolean; lastMessageAt: number | null; createdAt: number }
): number {
  if (left.pinned !== right.pinned) return left.pinned ? -1 : 1;
  return (
    (right.lastMessageAt ?? right.createdAt) -
    (left.lastMessageAt ?? left.createdAt)
  );
}

export function comparePinnedChannelOrder(
  left: { pinned: boolean },
  right: { pinned: boolean }
): number {
  if (left.pinned === right.pinned) return 0;
  return left.pinned ? -1 : 1;
}

export function compareJoinedChannelPreferenceOrder(
  left: {
    pinned: boolean;
    lastMessageAt: number | null;
    channel: { _creationTime: number };
  },
  right: {
    pinned: boolean;
    lastMessageAt: number | null;
    channel: { _creationTime: number };
  }
): number {
  return compareChannelPreferenceOrder(
    {
      pinned: left.pinned,
      lastMessageAt: left.lastMessageAt,
      createdAt: left.channel._creationTime,
    },
    {
      pinned: right.pinned,
      lastMessageAt: right.lastMessageAt,
      createdAt: right.channel._creationTime,
    }
  );
}
export type ChannelNotificationCounts = {
  mentionCount: number;
  unreadCount: number;
};

export function shouldToastChannelActivity({
  activeChannelId,
  channelId,
  current,
  previous,
  notificationMode,
  mutedUntil,
  now,
}: {
  activeChannelId: string;
  channelId: string;
  current: ChannelNotificationCounts;
  previous?: ChannelNotificationCounts;
  notificationMode: ChannelNotificationMode;
  mutedUntil: number | null;
  now: number;
}): boolean {
  if (!previous || activeChannelId === channelId) return false;
  const isMention = current.mentionCount > previous.mentionCount;
  if (!isMention && current.unreadCount <= previous.unreadCount) return false;
  return allowsChannelNotification({
    notificationMode,
    mutedUntil,
    isMention,
    now,
  });
}
